// Catalogs read for AI chat during this visit, shared by AiChatPanel (needs the
// search index) and CompareShelf (shows reading progress on each ribbon).
// Memory only: each PDF is fetched live from S3 and read once per visit, so
// switching between compared catalogs never re-reads one. Nothing is stored.
import { reactive, markRaw } from "vue";
import { buildIndex } from "../../lib/ai-poc/answer.mjs";
import { prepareCatalog } from "../../lib/ai-poc/pdftext.mjs";
import { loadPdfjs } from "./pdfjs";

// The largest catalogs (a few run to hundreds of MB) would stall a browser
// tab; nearly all catalogs are well under this.
const MAX_PDF_BYTES = 60 * 1024 * 1024;
// Up to three compared catalogs plus the one just closed; older ones are
// dropped to keep memory in check.
const KEEP = 4;

// id → { state, done, total }; state: reading → ready | too-large | error
const status = reactive({});
const indexes = new Map();
const pending = new Map();
const order = [];

export function readStatus(id) {
  return status[id] || { state: "reading", done: 0, total: 0 };
}

export function getIndex(id) {
  return indexes.get(String(id)) || null;
}

// Resolves to the catalog's search index, or null if it can't be read.
export function readCatalog(id, pdfUrl, name) {
  id = String(id);
  if (indexes.has(id)) return Promise.resolve(indexes.get(id));
  if (pending.has(id)) return pending.get(id);
  status[id] = { state: "reading", done: 0, total: 0 };
  const job = (async () => {
    try {
      const res = await fetch(pdfUrl);
      if (!res.ok) throw new Error(`PDF ${res.status}`);
      if (Number(res.headers.get("Content-Length")) > MAX_PDF_BYTES) {
        status[id] = { state: "too-large", done: 0, total: 0 };
        return null;
      }
      const data = new Uint8Array(await res.arrayBuffer());
      const pdfjs = await loadPdfjs();
      const catalog = await prepareCatalog(pdfjs, data, {
        name,
        onProgress: (done, total) => {
          status[id] = { state: "reading", done, total };
        },
      });
      // markRaw: the index is large and never needs to be reactive.
      indexes.set(id, markRaw(buildIndex(catalog)));
      status[id] = { state: "ready", done: catalog.pages.length, total: catalog.pages.length };
      order.push(id);
      while (order.length > KEEP) {
        const old = order.shift();
        indexes.delete(old);
        delete status[old];
      }
      return indexes.get(id);
    } catch (e) {
      console.error("[ai-chat] couldn't read catalog", id, e);
      status[id] = { state: "error", done: 0, total: 0 };
      return null;
    } finally {
      pending.delete(id);
    }
  })();
  pending.set(id, job);
  return job;
}

// Keep catalogs that are still in use from being dropped first.
export function touch(ids) {
  for (const id of ids.map(String)) {
    const i = order.indexOf(id);
    if (i > -1) order.push(...order.splice(i, 1));
  }
}
